import {
  SignIn as ClerkSignIn,
  SignUp as ClerkSignUp,
  SignedIn as ClerkSignedIn,
  SignedOut as ClerkSignedOut,
  useAuth as useClerkAuth,
  useClerk,
} from '@clerk/tanstack-react-start';
import { cloneElement, isValidElement, type MouseEventHandler, type ReactNode } from 'react';

export type AppAuthState = {
  isLoaded: boolean;
  isSignedIn: boolean;
  userId?: string | null;
  signOut: () => Promise<void>;
};

export function useAuth(): AppAuthState {
  const { isLoaded, userId } = useClerkAuth();
  const clerk = useClerk();

  return {
    isLoaded,
    isSignedIn: Boolean(userId),
    userId,
    signOut: () => clerk.signOut(),
  };
}

export function SignedIn({ children }: { children: ReactNode }) {
  return <ClerkSignedIn>{children}</ClerkSignedIn>;
}

export function SignedOut({ children }: { children: ReactNode }) {
  return <ClerkSignedOut>{children}</ClerkSignedOut>;
}

type SignInButtonProps = {
  children: ReactNode;
  redirectTo?: string;
};

export function getSignInRedirectUrl(origin: string, redirectTo: string) {
  const url = new URL('/login', origin);
  url.searchParams.set('redirect_url', redirectTo);
  return url.toString();
}

export function SignInButton({ children, redirectTo = '/app' }: SignInButtonProps) {
  const onClick: MouseEventHandler = (event) => {
    event.preventDefault();
    window.location.assign(getSignInRedirectUrl(window.location.origin, redirectTo));
  };

  if (isValidElement(children)) {
    return cloneElement(children, {
      onClick,
    } as { onClick: MouseEventHandler });
  }

  return (
    <button onClick={onClick} className="btn btn-primary">
      Sign in
    </button>
  );
}

export function SignIn() {
  return (
    <ClerkSignIn
      fallbackRedirectUrl="/app"
      signUpUrl="/signup"
      appearance={{
        elements: {
          rootBox: 'mx-auto',
          card: 'shadow-none bg-transparent',
        },
      }}
    />
  );
}

export function SignUp() {
  return (
    <ClerkSignUp
      fallbackRedirectUrl="/app"
      signInUrl="/login"
      appearance={{
        elements: {
          rootBox: 'mx-auto',
          card: 'shadow-none bg-transparent',
        },
      }}
    />
  );
}
